import { useEffect } from 'react';
import { Text } from '@belong/ui';
import { IconDots } from '@belong/icons';
import type { PaymentAccountResourceModel } from 'api/models';
import { PAYMENT_SCREENS } from 'containercomponents/Modals/PaymentsModal/PaymentsModal';
import PaymentMenuSelectionContainer from 'containers/PaymentSelectionMenuContainer/PaymentMenuSelectionContainer';
import { PaymentPreference } from 'models/enums';
import { FEEDBACK_STRINGS } from 'strings/errors.string';
import type {
  ConsumerFailedPaymentModel,
  ConsumerPaymentMethodSummaryModel,
} from './failed-payments.types';
import { FAILED_PAYMENTS_STRINGS } from './failed-payments-strings';
import { FailedPaymentBalanceSelection } from './failed-payment-balance-selection';
import {
  formatNegativeFailedPaymentsAmount,
  getFailedPaymentsTotal,
  getFailedPaymentsTotalForBalanceIds,
  mapPaymentAccountToPaymentMethodSummary,
} from './failed-payments-utils';
import { PaymentMethodSummary } from './payment-method-summary';
import { ResolveModalFooter } from './resolve-modal-footer';
import { CreditCardFeeBalanceRow } from './credit-card-fee-balance-row';

type RemainingFailedPaymentsPayNowStepProps = {
  failedPayments: ConsumerFailedPaymentModel[];
  selectedBalanceUniqueIds: string[];
  paymentMethods: PaymentAccountResourceModel[];
  selectedPaymentAccountUniqueId: string | null;
  paymentMethod?: ConsumerPaymentMethodSummaryModel | null;
  creditCardFeeAmount?: number;
  submitDisabled?: boolean;
  submitLoading?: boolean;
  onToggleBalanceIds: (balanceUniqueIds: string[]) => void;
  onSelectPaymentMethod: (id: string) => void;
  onSubmit: () => void;
};

export function RemainingFailedPaymentsPayNowStep({
  failedPayments,
  selectedBalanceUniqueIds,
  paymentMethods,
  selectedPaymentAccountUniqueId,
  paymentMethod,
  creditCardFeeAmount,
  submitDisabled = false,
  submitLoading = false,
  onToggleBalanceIds,
  onSelectPaymentMethod,
  onSubmit,
}: RemainingFailedPaymentsPayNowStepProps) {
  const availablePaymentMethods = paymentMethods.filter(
    (method) => method.accountId && method.isVerified,
  );
  const selectedPaymentMethod =
    availablePaymentMethods.find(
      (method) => method.accountId === selectedPaymentAccountUniqueId,
    ) ?? availablePaymentMethods[0];
  const selectedPaymentMethodSummary = selectedPaymentMethod
    ? mapPaymentAccountToPaymentMethodSummary(selectedPaymentMethod)
    : paymentMethod;
  const selectedPaymentTotal = getFailedPaymentsTotalForBalanceIds(
    failedPayments,
    selectedBalanceUniqueIds,
  );
  const submitTotal =
    selectedPaymentTotal > 0
      ? selectedPaymentTotal + (creditCardFeeAmount ?? 0)
      : getFailedPaymentsTotal(failedPayments);
  const submitLabel = `${
    FAILED_PAYMENTS_STRINGS['remaining.footer.pay_now']
  } | ${formatNegativeFailedPaymentsAmount(submitTotal)}`;

  useEffect(() => {
    if (!selectedPaymentAccountUniqueId && selectedPaymentMethod?.accountId) {
      onSelectPaymentMethod(selectedPaymentMethod.accountId);
    }
  }, [
    onSelectPaymentMethod,
    selectedPaymentAccountUniqueId,
    selectedPaymentMethod?.accountId,
  ]);

  const paymentModalProps = {
    screen: PAYMENT_SCREENS.PAYMENT_SELECTION,
    errorScreenProps: {
      title: FEEDBACK_STRINGS.set_payment_method.error.title,
      ctaLabel: FEEDBACK_STRINGS.set_payment_method.error.ctaLabel,
    },
    successScreenProps: {
      title: FEEDBACK_STRINGS.set_payment_method.success.title,
      ctaLabel: FEEDBACK_STRINGS.set_payment_method.success.ctaLabel,
    },
  };

  return (
    <div className="flex flex-col gap-2xl">
      <div className="flex flex-col gap-xs text-center">
        <Text variant="h2" fontWeight="semibold" className="leading-[normal]">
          <span className="block">
            {FAILED_PAYMENTS_STRINGS['remaining.title_line_1']}
          </span>
          <span className="block">
            {FAILED_PAYMENTS_STRINGS['remaining.title_line_2']}
          </span>
        </Text>
        <Text variant="body" className="whitespace-pre-line">
          {FAILED_PAYMENTS_STRINGS['remaining.subtitle']}
        </Text>
      </div>

      <div className="flex flex-col gap-md">
        <FailedPaymentBalanceSelection
          failedPayments={failedPayments}
          selectedBalanceUniqueIds={selectedBalanceUniqueIds}
          trailingRow={
            creditCardFeeAmount ? (
              <CreditCardFeeBalanceRow amount={creditCardFeeAmount} />
            ) : null
          }
          onToggleBalanceIds={onToggleBalanceIds}
        />

        <div className="flex items-center justify-between border-0 border-t border-dashed border-gray pt-sm">
          <Text fontWeight="semibold">
            {FAILED_PAYMENTS_STRINGS['section.total']}
          </Text>
          <Text fontWeight="semibold" className="text-right">
            {formatNegativeFailedPaymentsAmount(
              getFailedPaymentsTotal(failedPayments),
            )}
          </Text>
        </div>
      </div>

      <div className="flex flex-col gap-md">
        <Text variant="h3" fontWeight="semibold">
          {FAILED_PAYMENTS_STRINGS['remaining.payment_methods']}
        </Text>

        {selectedPaymentMethodSummary && (
          <PaymentMenuSelectionContainer
            selectedPaymentMethodId={selectedPaymentAccountUniqueId ?? ''}
            onSelectPaymentMethod={onSelectPaymentMethod}
            paymentType={PaymentPreference.Rent}
            paymentModalProps={paymentModalProps}
            showOnlyValidPaymentMethods
            shouldShowAutoSplitPaymentMethod={false}
            onSelectAutoSplitPaymentMethod={() => {}}
            fitToButton
            onTop
            menuButtonLabel={
              <span className="flex h-[72px] w-full items-center gap-md rounded border-0 bg-white text-left">
                <span className="min-w-0 flex-1">
                  <PaymentMethodSummary
                    paymentMethod={selectedPaymentMethodSummary}
                  />
                </span>
                <span className="hidden text-body font-semibold uppercase text-navy sm:inline">
                  {FAILED_PAYMENTS_STRINGS['modal.entry.change']}
                </span>
                <span className="inline-flex px-xs text-navy sm:hidden">
                  <IconDots width={5} height={24} />
                </span>
              </span>
            }
          />
        )}
      </div>

      <ResolveModalFooter
        onSubmit={onSubmit}
        submitLabel={submitLabel}
        disabled={submitDisabled}
        loading={submitLoading}
      />
    </div>
  );
}
